import { useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import usePageSEO from '@/hooks/usePageSEO';
import { videos } from '@/mocks/videos';

const categoryNameMap: Record<string, string> = {
  dianshiju: '电视剧',
  dianying: '电影',
  dongman: '动漫',
  zongyi: '综艺',
  jilupian: '纪录片',
  jingsong: '动作片',
  aiqing: '爱情片',
  xiju: '喜剧片',
};

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const video = useMemo(() => videos.find((v) => v.id === Number(id)), [id]);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.id !== video.id && v.category === video.category)
      .slice(0, 4);
  }, [video]);

  usePageSEO({
    title: video ? `${video.title} - 人人影视高清免费在线观看` : '人人影视 - 影视详情',
    description: video ? `${video.title}：${video.description} 主演：${video.actors.join('、')}，导演：${video.director}。人人影视高清免费在线观看，不缓冲秒播。` : '人人影视高清免费在线观看影视详情',
    keywords: video ? `人人影视,${video.title},人人影视高清,人人影视免费,高清免费在线观看,${video.actors.slice(0, 2).join(',')}` : '人人影视,高清免费在线观看',
    canonicalPath: `/detail/${id || ''}`,
    ogType: 'article',
    jsonLd: video ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': `${video.title} - 人人影视高清免费在线观看`,
      'description': video.description,
      'datePublished': `${video.year}-01-01`,
      'url': `https://www.renrentv.com/detail/${video.id}`,
      'author': {
        '@type': 'Organization',
        'name': '人人影视',
      },
      'publisher': {
        '@type': 'Organization',
        'name': '人人影视',
      },
      'breadcrumb': {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.renrentv.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': categoryNameMap[video.category] || video.category, 'item': `https://www.renrentv.com/category/${video.category}` },
          { '@type': 'ListItem', 'position': 3, 'name': video.title },
        ],
      },
    } : undefined,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  if (!video) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-lg text-foreground-500 mb-4">未找到该影视资源</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryNameMap[video.category] || video.category, path: `/category/${video.category}` },
                { label: video.title },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-64 lg:w-72 aspect-[3/4] rounded-2xl overflow-hidden">
                  <img
                    src={video.cover}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {video.title}
                </h1>

                <div className="flex items-center flex-wrap gap-3 mb-4">
                  <span className="flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="font-semibold text-foreground-900">{video.rating}</span>
                  </span>
                  <span className="text-sm text-foreground-400">{video.year}</span>
                  <span className="text-sm text-foreground-400">{video.region}</span>
                  <span className="px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                    {categoryNameMap[video.category] || video.category}
                  </span>
                  <span className="px-2.5 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full">
                    {video.status}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-5 max-w-2xl">
                  {video.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-400 flex-shrink-0">导演：</span>
                    <span className="text-foreground-800">{video.director}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-400 flex-shrink-0">主演：</span>
                    <span className="text-foreground-800">{video.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-400 flex-shrink-0">集数：</span>
                    <span className="text-foreground-800">{video.episodes > 1 ? `${video.episodes}集` : '单集'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-400 flex-shrink-0">状态：</span>
                    <span className="text-foreground-800">{video.status}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full 
                    text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill text-xl"></i>
                    立即播放
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 
                    text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 
                    text-foreground-600 hover:bg-secondary-100 hover:text-secondary-600 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>

                {video.episodes > 1 && (
                  <div className="mt-8">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-3">剧集列表</h3>
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: Math.min(video.episodes, 24) }, (_, i) => (
                        <button
                          key={i}
                          className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-sm 
                            text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          {i + 1}
                        </button>
                      ))}
                      {video.episodes > 24 && (
                        <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-sm 
                          text-foreground-400 cursor-pointer">
                          ...
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {relatedVideos.length > 0 && (
              <div className="mt-12 md:mt-16">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-bold text-foreground-950">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedVideos.map((v) => (
                    <Link
                      key={v.id}
                      to={`/detail/${v.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                        <img
                          src={v.cover}
                          alt={v.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded-md">
                            {v.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {v.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-1">{v.year}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}